import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

import styles from "./weekSchedule.module.scss";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Plain YYYY-MM-DD maths in UTC, as the schedule popups do, so no local
// timezone or DST shift can move a day. The server sends the real "today".
const addDays = (iso, amount) => {
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    date.setUTCDate(date.getUTCDate() + amount);
    return date.toISOString().slice(0, 10);
};

const startOfWeek = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
    return addDays(iso, weekday === 0 ? -6 : 1 - weekday);
};

const dayOfMonth = (iso) => Number(iso.slice(8, 10));

const prettyDate = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        timeZone: "UTC",
    });
};

/**
 * What a banner shows each day of a week, read-only, on its edit page — so
 * who or what is scheduled can be seen without opening the schedule popup.
 *
 * Shared by the Picks banner (people) and the Featured Summary card (books);
 * both schedule endpoints answer `{ today, entries: [{ date, items }] }`.
 *
 * - `endpoint` — the section's `/schedule` URL; fetched a week at a time.
 * - `toItem(item)` — maps one entry item to `{ id, name, image }`.
 * - `fallback` — `{ name, image }` the app serves on an unscheduled day (the
 *   section's own pick), shown dimmed and marked "Default".
 * - `shape` — "round" for people, "cover" for books.
 * - `reloadKey` — bump it after the popup saves, to refetch.
 * - `onEdit` — opens the schedule popup.
 * - `onSelectDay(date)` — optional; makes each day a button that opens it.
 */
const WeekSchedule = ({ endpoint, token, toItem, fallback, shape = "round", reloadKey, onEdit, onSelectDay }) => {
    const [weekStart, setWeekStart] = useState(() =>
        startOfWeek(new Date().toLocaleDateString("en-CA"))
    );
    const [today, setToday] = useState("");
    const [byDate, setByDate] = useState({});
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    const dates = useMemo(() => DAY_NAMES.map((_, i) => addDays(weekStart, i)), [weekStart]);

    useEffect(() => {
        if (!endpoint || !token) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setFailed(false);
            try {
                const { data: response } = await axios.get(endpoint, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { from: weekStart, to: addDays(weekStart, 6) },
                });
                if (cancelled) return;
                if (!response.status) throw new Error(response.message);
                const next = {};
                for (const entry of response.data?.entries || []) {
                    next[entry.date] = { items: entry.items || [] };
                }
                setByDate(next);
                if (response.data?.today) setToday(response.data.today);
            } catch (error) {
                console.error("WeekSchedule load error::", error);
                if (!cancelled) {
                    setByDate({});
                    setFailed(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [endpoint, token, weekStart, reloadKey]);

    const scheduledCount = dates.filter((d) => byDate[d]?.items?.length).length;

    return (
        <div className={styles.wrap}>
            <div className={styles.toolbar}>
                <div>
                    <div className={styles.heading}>Schedule</div>
                    <div className={styles.sub}>
                        {failed
                            ? "Couldn't load the schedule."
                            : loading
                                ? "Loading…"
                                : `${scheduledCount} of 7 days scheduled — the rest show the default.`}
                    </div>
                </div>
                <div className={styles.controls}>
                    <button
                        type="button"
                        className={styles.navBtn}
                        title="Previous week"
                        onClick={() => setWeekStart((w) => addDays(w, -7))}
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <span className={styles.range}>
                        {prettyDate(dates[0])} – {prettyDate(dates[6])}
                    </span>
                    <button
                        type="button"
                        className={styles.navBtn}
                        title="Next week"
                        onClick={() => setWeekStart((w) => addDays(w, 7))}
                    >
                        <ChevronRight size={16} />
                    </button>
                    {today && !dates.includes(today) && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setWeekStart(startOfWeek(today))}
                        >
                            This week
                        </button>
                    )}
                    {onEdit && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-2"
                            onClick={onEdit}
                        >
                            <CalendarDays size={16} /> Edit schedule
                        </button>
                    )}
                </div>
            </div>

            <div className={styles.grid}>
                {dates.map((date, i) => {
                    const entry = byDate[date];
                    const scheduled = (entry?.items || []).map(toItem);
                    const shown = scheduled.length ? scheduled : fallback?.name ? [fallback] : [];
                    const isToday = date === today;
                    const isPast = today && date < today;

                    // Openable days act as buttons — focusable, Enter/Space —
                    // without being a <button>, which can't contain the
                    // tile's block content.
                    const open = onSelectDay && {
                        role: "button",
                        tabIndex: 0,
                        onClick: () => onSelectDay(date),
                        onKeyDown: (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onSelectDay(date);
                            }
                        },
                        title: isPast ? "View this day" : "Edit this day",
                        "aria-label": `${isPast ? "View" : "Edit"} ${prettyDate(date)}`,
                    };

                    return (
                        <div
                            key={date}
                            {...open}
                            className={[
                                styles.day,
                                onSelectDay ? styles.clickable : "",
                                isToday ? styles.today : "",
                                isPast ? styles.past : "",
                            ].join(" ")}
                        >
                            <div className={styles.dayHead}>
                                <span>{DAY_NAMES[i]}</span>
                                <span className={styles.dayNum}>{dayOfMonth(date)}</span>
                                {isToday && <span className={styles.todayTag}>Today</span>}
                            </div>

                            {shown.length ? (
                                <div className={scheduled.length ? "" : styles.isDefault}>
                                    {shown.map((item, k) => (
                                        <div key={item.id || k} className={styles.item}>
                                            {item.image ? (
                                                <img
                                                    src={item.image}
                                                    alt={item.name}
                                                    className={shape === "cover" ? styles.cover : styles.avatar}
                                                />
                                            ) : (
                                                <div className={`${shape === "cover" ? styles.cover : styles.avatar} ${styles.blank}`} />
                                            )}
                                            <span className={styles.name} title={item.name}>{item.name}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className={styles.empty}>Nothing set</div>
                            )}

                            <div className={styles.footer}>
                                <span className={scheduled.length ? styles.tagScheduled : styles.tagDefault}>
                                    {scheduled.length ? "Scheduled" : "Default"}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default WeekSchedule;
